import React from "react";
import { useWizard } from "@/context/WizardContext";
import { Grid, SelectInput, SectionHeading, FieldLabel } from "@/components/wizard/FormKit";
import NarrativeField from "@/components/wizard/NarrativeField";

const CAPABILITY_OPTS = [
  { value: "1", label: "1 — Communicates needs verbally" },
  { value: "2", label: "2 — Verbal with difficulty, understandable" },
  { value: "3", label: "3 — Sign/symbol/gestures/interpreter" },
  { value: "4", label: "4 — Inappropriate content / garbled" },
  { value: "5", label: "5 — Does not communicate needs" },
  { value: "6", label: "6 — Child, age-appropriate" },
];

const HEARING_OPTS = [
  { value: "1", label: "1 — No hearing impairment" },
  { value: "2", label: "2 — Difficulty at conversation level" },
  { value: "3", label: "3 — Only very loud sounds" },
  { value: "4", label: "4 — No useful hearing" },
  { value: "5", label: "5 — Not determined" },
];

const VISION_OPTS = [
  { value: "1", label: "1 — No impairment" },
  { value: "2", label: "2 — Difficulty with print" },
  { value: "3", label: "3 — Difficulty seeing obstacles" },
  { value: "4", label: "4 — No useful vision" },
  { value: "5", label: "5 — Not determined" },
];

const ORIENT_LEVELS = [
  { value: "oriented", label: "Oriented" },
  { value: "totally_disoriented", label: "Totally disoriented" },
  { value: "comatose", label: "Comatose" },
  { value: "not_determined", label: "Not determined" },
];

function MultiCheck({ label, options, selected, onChange, testidPrefix }) {
  const toggle = (v) => {
    const set = new Set(selected || []);
    if (set.has(v)) set.delete(v); else set.add(v);
    onChange([...set]);
  };
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const on = (selected || []).includes(o.value);
          return (
            <button type="button" key={o.value} onClick={() => toggle(o.value)}
              data-testid={`${testidPrefix}-${o.value}`}
              className={`px-3 h-9 rounded-full text-[12.5px] font-medium border transition ${on ? "bg-[#0A3D79] text-[#F7FAFC] border-[#0A3D79]" : "bg-white text-[#0B1E3A] border-[#D6E3EF] hover:border-[#1FA0E6]"}`}>
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Step4Comm() {
  const { data, update } = useWizard();
  return (
    <div className="space-y-10">
      <section className="card-surface fade-up">
        <SectionHeading eyebrow="Step 4 · F-11136 Element 6" title="Communication Capability" />
        <Grid cols={1}>
          <SelectInput label="Capability code" options={CAPABILITY_OPTS}
            value={data.communication.capability_code}
            onChange={(v) => update("communication", { capability_code: v })}
            testid="comm-capability" />
        </Grid>
      </section>

      <section className="card-surface fade-up">
        <SectionHeading eyebrow="Step 4 · F-11136 Element 7" title="Hearing Aid Usage" />
        <Grid cols={2}>
          <SelectInput label="Does the member wear a hearing aid?" options={["Yes","No"]}
            value={data.hearing_aid?.wears_aid}
            onChange={(v) => update("hearing_aid", { wears_aid: v })}
            testid="comm-hearing-wears" />
          <SelectInput label="If yes, ability with hearing aid" options={HEARING_OPTS}
            value={data.hearing_aid?.ability_code}
            onChange={(v) => update("hearing_aid", { ability_code: v })}
            testid="comm-hearing-ability" />
        </Grid>
      </section>

      <section className="card-surface fade-up">
        <SectionHeading eyebrow="Step 4 · F-11136 Element 8" title="Vision Correction" />
        <Grid cols={2}>
          <SelectInput label="Does the member wear corrective lenses?" options={["Yes","No"]}
            value={data.vision_correction?.wears_lenses}
            onChange={(v) => update("vision_correction", { wears_lenses: v })}
            testid="comm-vision-wears" />
          <SelectInput label="If yes, ability with correction" options={VISION_OPTS}
            value={data.vision_correction?.ability_code}
            onChange={(v) => update("vision_correction", { ability_code: v })}
            testid="comm-vision-ability" />
        </Grid>
      </section>

      <section className="card-surface fade-up">
        <SectionHeading eyebrow="Step 4 · F-11136 Element 9" title="Orientation" />
        <Grid cols={1}>
          <SelectInput label="Overall level" options={ORIENT_LEVELS}
            value={data.orientation?.level}
            onChange={(v) => update("orientation", { level: v })}
            testid="comm-orient-level" />
        </Grid>
        <div className="mt-5 space-y-5">
          <MultiCheck label="Minor forgetfulness of..." testidPrefix="comm-forget"
            selected={data.orientation?.forgetfulness}
            onChange={(v) => update("orientation", { forgetfulness: v })}
            options={[
              { value: "time", label: "Time" },
              { value: "place", label: "Place" },
              { value: "person", label: "Person" },
              { value: "medications", label: "Medications" },
              { value: "meals", label: "Meals" },
            ]} />
          <MultiCheck label="Partial/intermittent disorientation..." testidPrefix="comm-diso"
            selected={data.orientation?.disorientation}
            onChange={(v) => update("orientation", { disorientation: v })}
            options={[
              { value: "am", label: "a.m." },
              { value: "pm", label: "p.m." },
              { value: "two_hours_or_less", label: "2 hrs or less" },
              { value: "consistently", label: "Consistently" },
              { value: "inconsistently", label: "Inconsistently" },
            ]} />
        </div>
      </section>

      <section className="card-surface fade-up">
        <SectionHeading eyebrow="Step 4 · Additional" title="Behavioral / Mood Narrative" />
        <NarrativeField label="Behavioral concerns, mood, safety"
          fieldKey="communication.behavior"
          fieldPurpose="PCST Element 31 context — document behaviors, mood, and safety that affect ADL/MOT delivery."
          testid="comm-behavior"
          value={data.communication.behavior}
          onChange={(v) => update("communication", { behavior: v })} />
      </section>
    </div>
  );
}
